import { LuiMaterialTable } from '@jda/lui-common-component-library';
import { Box, TablePagination, TextField, Button, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import * as React from 'react';
import _ from 'lodash';
import Pagination from '@material-ui/lab/Pagination';
import { DispatchContext, StateContext } from '../../App';
import { useContext } from 'react';
import { MTableToolbar } from 'material-table';
import PageInfo from '../../components/PageInfo';
import PageNumber from './pageNumber';

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  className?: string | undefined;
  onChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = (props: TablePaginationActionsProps) => {
  const [pagesCount, setPagesCount] = React.useState(0);
  const { count, page, rowsPerPage, onChangePage } = props;
  const handleChange = (event: any, value: number) => {
    onChangePage(event, value - 1);
  };

  React.useEffect(() => {
    setPagesCount(Math.ceil(count / rowsPerPage));
  }, [count, rowsPerPage]);
  return (
    <Box className={props.className}>
      <Pagination
        count={pagesCount}
        page={page + 1}
        boundaryCount={1}
        onChange={handleChange}
        variant="text"
        color="primary"
        data-id="pagination-box"
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonMargin: {
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(1)
    },
    priorityBarSettings: {
      position: 'relative',
      height: theme.spacing(4),
      width: theme.spacing(0.25)
    },
    root: {
      width: '100%',
      backgroundColor: theme.palette.background.paper,
      borderRadius: '6px'
    }
  })
);

type TableProps = {
  id: any;
  data: any;
  columns: any;
  subColumns?: any;
  showSelection?: true | false;
  showSorting?: true | false;
  showPagination?: true | false;
  showTopPagination?: true | false;
  showSpecificPage?: true | false;
  title?: string;
  onSelection?: (rows: any, rowData: any) => void;
  tableActions?: Array<any>;
  page?: number;
  rowsPerPage?: number;
  labelRowsPerPage?: string;
  count?: number;
  serverPagination?: true | false;
  showTitle?: true | false;
  showToolbar?: true | false;
  detailPanel?: any;
  showSearch?: true | false;
  actionsColumnIndex?: number;
  inputText?: String;
  onClearText?: any;
  onSearchChange?: (string: any) => void;
  pageLevelInfo?: any;
  getData?: (value: any) => void;
  handlePageNumber?: any;
  isAllSelected?: true | false;
};

const DetailedTable = (props: TableProps) => {
  const classes = useStyles();
  // const theme = useTheme();
  const [page, setPage] = React.useState(props.page || 0);
  const [gotoPage, setGotoPage] = React.useState<any>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(props.rowsPerPage || 10);
  const dispatch = useContext(DispatchContext);
  const state = useContext(StateContext);
  const { pageLevelInfo } = props;

  React.useEffect(() => {
    const tableId = props.id;
    state?.userPreferences?.[tableId]?.page && setPage(state.userPreferences[tableId].page);
    state?.userPreferences?.[tableId]?.rowsPerPage && setRowsPerPage(state.userPreferences[tableId].rowsPerPage);
  }, [props.id, state]);

  React.useEffect(() => {
    if (props?.inputText) {
      setPage(props.page || 0);
    } else if (props?.page) {
      setPage(props.page || 0);
    }
  }, [props.inputText, props.page, props.data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (!props?.inputText) {
      dispatch({
        type: 'userPreferences',
        data: {
          [props.id]: {
            id: props.id,
            rowsPerPage: rowsPerPage,
            page: newPage
          }
        }
      });
    }
    if (props?.getData) props.getData(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);

    dispatch({
      type: 'userPreferences',
      data: {
        [props.id]: {
          id: props.id,
          rowsPerPage: +event.target.value,
          page: 0
        }
      }
    });
  };

  const finalData =
    props.data && props.showPagination
      ? props.data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : props.data;

  const materialTableOptions = {
    sorting: props.showSorting ? true : false,
    showTitle: props.showTitle ? props.showTitle : true,
    toolbar: props.showToolbar ? props.showToolbar : true,
    paging: false,
    search: props.showSearch ? props.showSearch : false,
    showTextRowsSelected: false,
    showSelectAllCheckbox: props.isAllSelected ? props.isAllSelected : false,
    selection: props.showSelection ? props.showSelection : false,
    actionsColumnIndex: _.isNumber(props.actionsColumnIndex) ? props.actionsColumnIndex : -1,
    headerStyle: {
      backgroundColor: '#EBEBEB',
      color: '#3F4756 '
    },
    maxColumnSort: 2,
    showColumnSortOrder: props.showSorting ? true : false,
    thirdSortClick: props.showSorting ? true : false
  };

  const handleDataChange = (e: any) => {
    setGotoPage(e.target.value);
  };

  const handleGotoSubmit = (pageNumber: any) => {
    if (props?.serverPagination) {
      props.handlePageNumber(pageNumber - 1);
      dispatch({
        type: 'userPreferences',
        data: {
          [props.id]: {
            id: props.id,
            rowsPerPage: rowsPerPage,
            page: pageNumber - 1
          }
        }
      });
    }
    setPage(pageNumber - 1);
    setGotoPage('');
  };

  const SetPageNumber = () => <>{props?.showSpecificPage && <PageNumber handleGotoSubmit={handleGotoSubmit} />}</>;

  return (
    <Box className={classes.root}>
      {props.showTopPagination ? (
        <>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={props.count || props?.data?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            data-id="pagination"
            onChangePage={handleChangePage}
            labelRowsPerPage={props.labelRowsPerPage || 'Items per page:'}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            ActionsComponent={(props: any) => (
              <>
                <TablePaginationActions {...props} />
                <SetPageNumber />
                <PageInfo pagination={true} pageLevelInfo={pageLevelInfo} />
              </>
            )}
          />
        </>
      ) : (
        <PageInfo pagination={false} pageLevelInfo={pageLevelInfo} />
      )}
      <>
        <LuiMaterialTable
          key={props.title}
          data={props.serverPagination ? props.data : finalData}
          columns={props.columns}
          materialTableProps={{
            title: props.title,
            actions: props.tableActions ? props.tableActions : [],
            options: materialTableOptions,
            onSelectionChange: props.onSelection,
            onSearchChange: props.onSearchChange,
            detailPanel: props.detailPanel,
            components: {
              Toolbar: MTableToolbar
            }
          }}
        ></LuiMaterialTable>
      </>
      {props.showPagination ? (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={props.count || props?.data?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          data-id="pagination"
          onChangePage={handleChangePage}
          labelRowsPerPage={props.labelRowsPerPage || 'Items per page:'}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={(props: any) => (
            <>
              <TablePaginationActions {...props} />
              <SetPageNumber />
            </>
          )}
        />
      ) : (
        ''
      )}
    </Box>
  );
};

export default DetailedTable;
